import { GlobalErrorType } from '@brunommsilva/react-foundations';
import { Refresh, Warning, WifiOff } from '@mui/icons-material';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  errorType: GlobalErrorType | null;
  onRetry: () => void;
}

const GlobalErrorPage = ({ errorType, onRetry }: Props) => {
  const { t } = useTranslation();
  const connectionErrorTypes = [
    GlobalErrorType.CONNECTION,
    GlobalErrorType.OFFLINE,
  ];
  const isConnection = errorType && connectionErrorTypes.includes(errorType);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
          gap: 3,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.lighter',
            color: 'error.main',
            p: 3,
            borderRadius: '50%',
            boxShadow: '0 8px 16px rgba(211, 47, 47, 0.1)',
            mb: 1,
            animation: 'pulse 2s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)',
              },
              '70%': {
                transform: 'scale(1.05)',
                boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
              },
            },
          }}
        >
          {isConnection ? (
            <WifiOff sx={{ fontSize: 56 }} />
          ) : (
            <Warning sx={{ fontSize: 56 }} />
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: 'text.primary' }}
        >
          {isConnection ? t('CONNECTION_ERROR') : t('CRITICAL_ERROR')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: '380px',
            lineHeight: 1.6,
          }}
        >
          {isConnection ? t('OFFLINE_DESC') : t('SERVER_ERROR_DESC')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={onRetry}
          startIcon={<Refresh />}
          sx={{
            py: 1.8,
            px: 5,
            borderRadius: 3,
            fontWeight: 800,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.25)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 28px rgba(25, 118, 210, 0.35)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {t('RETRY')}
        </Button>
      </Box>
    </Container>
  );
};

export default GlobalErrorPage;
